import {
  CtrlBaseSuggestionArgs,
  RegValvesSuggestionArgs,
} from "@imi-ernd-be/hyselect-engine";
import { TechnicalSettings } from "@imi/api-client/index.js";
import { CustomPipeSeries } from "@src/core/api/customPipeSeries";
import { PipeSeriesPreference } from "@src/core/api/pipeSeriesPreference";
import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { isFunction } from "@tanstack/react-table";
import { engine } from "../..";
import { GetSuggestionsFilters } from "../technicalSelectionRepository";
import { initBalancingControlValvesSuggestionArgs } from "./initBalancingControlValvesSuggestionArgs";
import { initControlValvesSuggestionArgs } from "./initControlValvesSuggestionArgs";
import { initPibcvSuggestionArgs } from "./initPibcvSuggestionArgs";
import { initRegulatingValvesSuggestionArgs } from "./initRegulatingValvesSuggestionArgs";
import { initSmartControlValvesSuggestionArgs } from "./initSmartControlValvesSuggestionArgs";
import { initSmartDpControllersSuggestionArgs } from "./initSmartDpControllersSuggestionArgs";
import {
  ActuatorSelectionArgsOptions,
  initActuatorsSuggestionArgs,
} from "./subProducts/initActuatorsSuggestionArgs";
import {
  initSensorSuggestionArgs,
  SensorSelectionArgsOptions,
} from "./subProducts/initSensorSuggestionArgs";

export type SuggestionArgsOptions = (
  | { subProductType?: never }
  | ActuatorSelectionArgsOptions
  | SensorSelectionArgsOptions
) &
  ValveSuggestionArgsOptions;

export type ValveSuggestionArgsOptions = {
  categoryId: string;
  customPipeSeries: CustomPipeSeries[];
  filters: GetSuggestionsFilters;
  pipeSeriesPreference: PipeSeriesPreference;
  technicalSettings: TechnicalSettings | null;
  waterCharacteristics: WaterCharacteristicsPreference | null;
};

export function initSuggestionArgs(options: SuggestionArgsOptions) {
  const { cleanup, currentSelection, suggestionArgs } =
    initValveSuggestionArgs(options);
  if (!suggestionArgs) {
    return { cleanup, suggestionArgs: null };
  }

  const technicalSelection = new engine.TechnicalSelection();
  if (
    isCtrlBaseSuggestionArgs(suggestionArgs) &&
    options.subProductType === "actuators"
  ) {
    initActuatorsSuggestionArgs(
      technicalSelection,
      currentSelection!,
      suggestionArgs,
      options,
    );
  }

  if (
    suggestionArgs instanceof engine.SmartDpControllerSuggestionArgs &&
    options.subProductType === "sensors"
  ) {
    initSensorSuggestionArgs(technicalSelection, suggestionArgs, options);
  }

  return {
    cleanup() {
      cleanup();
      technicalSelection.delete();
    },
    suggestionArgs,
  };
}

function initValveSuggestionArgs(options: SuggestionArgsOptions) {
  switch (options.categoryId) {
    case "TS_BCV_Category":
      return initBalancingControlValvesSuggestionArgs(options);
    case "TS_CV_Category":
      return initControlValvesSuggestionArgs(options);
    case "TS_PIBCV_Category":
      return initPibcvSuggestionArgs(options);
    case "TS_REGV_Category":
      return initRegulatingValvesSuggestionArgs(options);
    case "TS_SMRTCV_Category":
      return initSmartControlValvesSuggestionArgs(options);
    case "TS_SMRTDP_Category":
      return initSmartDpControllersSuggestionArgs(options);
    default:
      return { cleanup() {}, currentSelection: null, suggestionArgs: null };
  }
}

function isCtrlBaseSuggestionArgs(
  args: RegValvesSuggestionArgs,
): args is CtrlBaseSuggestionArgs {
  return (
    "saveActuatorsFilters" in args && isFunction(args.saveActuatorsFilters)
  );
}
